import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";

import "./PublicLandingFooter.css";

import { LayoutContainerModel } from "../../../../models/containers/layout/LayoutContainerModel";
import { AssetsService } from "../../../../services/data/assets.service";
import { LayoutsService } from "../../../../services/layout/layouts.service";
import { globalUriLoader } from "../../../../services/singletons/generic/global.uri.loader";
import { filterOptimizeMemory } from "../../../../hooks/utils/CommonUtils";

const BasicLogoCard = lazy(() =>
    import("../../../cards/generic/BasicLogoCard")
        .then(module => ({ default: module.BasicLogoCard }))
);

const RAVerifiedSeal = lazy(async () => {
    const module = await import("../../../buttons/marketing/social/reclame-aqui/RAVerifiedSeal");
    const component = (module as Record<string, unknown>).RAVerifiedSeal ?? (module as Record<string, unknown>).default;

    return { default: component as React.ComponentType<any> };
});


export default function PublicLandingFooter () {
    const initialized = useRef(false);
        
    const layoutsService = useRef(new LayoutsService());
    const assetsService = useRef(new AssetsService());

    const [contactModels, setContactModels] = useState<Array<LayoutContainerModel>>([]);

    const [logoDeliveryDiretoSrc, setLogoDeliveryDiretoSrc] = useState<string>("");
    const [logoIfoodSrc, setLogoIfoodSrcSrc] = useState<string>("");
    const [logo99Src, setLogo99SrcSrc] = useState<string>("");
    const [logoDiblasiPrimarySrc, setLogoDiblasiPrimarySrc] = useState<string>("");

    const [logoOGloboSrc, setLogoOGloboSrc] = useState<string>("");
    const [logoEmpresasNegociosSrc, setLogoEmpresasNegociosSrc] = useState<string>("");
    
    const [textoRodape, setTextoRodape] = useState<string>("");
    
    useEffect(() => {        
        if (!initialized.current) {
            initialized.current = true;

            const fetchLayoutAssets = async () => {
                setLogoDiblasiPrimarySrc(await globalUriLoader.getDependentAssetUri("layout-logo-di-blasi-principal"));
            };

            const fetchContainers = async () => {
                let containers: LayoutContainerModel[] | undefined =
                    await layoutsService.current.handleListLayoutContainersWithRange(
                        1,
                        100,
                        undefined,
                        undefined,
                        "layout",
                        5,
                        true
                );

                if (containers !== undefined) {
                    let toDeleteIds: number[] = [];

                    for (const container of containers) {
                        let i: number = 0;

                        if (container.position < 4 && container.tag.startsWith("layout-rodape-contato")) {
                            i++;
                            container.content = assetsService.current.getDependentAssetUri(container.content);
                            setContactModels(prev => [...prev, container]);
                            toDeleteIds.push(container.id!);

                            if (i === 2) {
                                break;
                            }
                        }
                    }
                    
                    containers = filterOptimizeMemory(containers, toDeleteIds);

                    for (const container of containers) {
                        if (container.position === 1 && container.tag.startsWith("layout-third-party-logo")) {
                            setLogoDeliveryDiretoSrc(assetsService.current.getDependentAssetUri(container.content));
                            toDeleteIds.push(container.id!);

                            break;
                        }
                    }

                    containers = filterOptimizeMemory(containers, toDeleteIds);

                    for (const container of containers) {
                        if (container.position === 2 && container.tag.startsWith("layout-third-party-logo")) {
                            setLogoIfoodSrcSrc(assetsService.current.getDependentAssetUri(container.content));
                            toDeleteIds.push(container.id!);

                            break;
                        }
                    }

                    containers = filterOptimizeMemory(containers, toDeleteIds);

                    for (const container of containers) {
                        if (container.position === 3 && container.tag.startsWith("layout-third-party-logo")) {
                            setLogo99SrcSrc(assetsService.current.getDependentAssetUri(container.content));
                            toDeleteIds.push(container.id!);

                            break;
                        }
                    }

                    containers = filterOptimizeMemory(containers, toDeleteIds);

                    for (const container of containers) {
                        if (container.position === 4 && container.tag.startsWith("layout-third-party-logo")) {
                            setLogoOGloboSrc(assetsService.current.getDependentAssetUri(container.content));
                            toDeleteIds.push(container.id!);

                            break;
                        }
                    }

                    containers = filterOptimizeMemory(containers, toDeleteIds);

                    for (const container of containers) {
                        if (container.position === 5 && container.tag.startsWith("layout-third-party-logo")) {
                            setLogoEmpresasNegociosSrc(assetsService.current.getDependentAssetUri(container.content));
                            toDeleteIds.push(container.id!);

                            break;
                        }
                    }

                    containers = filterOptimizeMemory(containers, toDeleteIds);

                    for (const container of containers) {
                        if (container.position === 1 && container.tag.startsWith("layout-rodape-texto")) {
                            setTextoRodape(container.content);
                            break;
                        }
                    }
                    
                    containers.length = 0;
                }
            };

            fetchContainers();
            fetchLayoutAssets();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    
    return (
        <Container fluid style={{backgroundColor: "#1d1d1d", color: "white"}}>
            <div className="large-vertical-combo-spacer"></div>
            <Row>
                <Col>
                    <div style={{width: "100%", display: "flex", justifyContent: "center"}}>
                        <Suspense fallback={null}>
                            <BasicLogoCard
                                width="128px"
                                height="128px"
                                model = {logoDeliveryDiretoSrc ? {
                                    imageSrc: logoDeliveryDiretoSrc,
                                    backgroundColor: "none",
                                    buttonClick: () => {}
                                } : undefined}
                            />
                        </Suspense>
                    </div>
                </Col>
                <Col>
                    <div style={{width: "100%", display: "flex", justifyContent: "center"}}>
                        <Suspense fallback={null}>
                            <BasicLogoCard
                                width="128px"
                                height="128px"
                                model = {logoIfoodSrc ? {
                                    imageSrc: logoIfoodSrc,
                                    backgroundColor: "none",
                                    buttonClick: () => {}
                                } : undefined}
                            />
                        </Suspense>
                    </div>
                </Col>
                <Col>
                    <div style={{width: "100%", display: "flex", justifyContent: "center"}}>
                        <Suspense fallback={null}>
                            <BasicLogoCard
                                width="128px"
                                height="128px"
                                model = {logo99Src ? {
                                    imageSrc: logo99Src,
                                    backgroundColor: "none",
                                    buttonClick: () => {}
                                } : undefined}
                            />
                        </Suspense>
                    </div>
                </Col>
            </Row>
            <div className="large-vertical-combo-spacer"></div>
            <Row>
                <Col>
                    <div style={{width: "100%", display: "flex", justifyContent: "center"}}>
                        <Suspense fallback={null}>
                            <BasicLogoCard
                                width="128px"
                                height="128px"
                                model = {logoOGloboSrc ? {
                                    imageSrc: logoOGloboSrc,
                                    backgroundColor: "none",
                                    buttonClick: () => {}
                                } : undefined}
                            />
                        </Suspense>
                    </div>
                </Col>
                {
                    logoDiblasiPrimarySrc &&
                    <Col>
                        <img src={logoDiblasiPrimarySrc} alt="Logo" style={{maxWidth: "256px"}}></img>
                    </Col>
                }
                <Col>
                    <div style={{width: "100%", display: "flex", justifyContent: "center"}}>
                        <Suspense fallback={null}>
                            <BasicLogoCard
                                width="128px"
                                height="128px"
                                model = {logoEmpresasNegociosSrc ? {
                                    imageSrc: logoEmpresasNegociosSrc,
                                    backgroundColor: "none",
                                    buttonClick: () => {}
                                } : undefined}
                            />
                        </Suspense>
                    </div>
                </Col>
            </Row>
            <div className="large-vertical-combo-spacer"></div>
            <Row>
                <Col>
                    <div 
                        dangerouslySetInnerHTML={{ __html: textoRodape }} 
                    />
                </Col>
            </Row>
            <div className="large-vertical-combo-spacer"></div>
            <Row>
                <Col>
                    <table className="footer-left-align-table">
                        <thead></thead>
                        <tbody>
                            <tr className="table-primary">
                                <td>
                                    <img src={contactModels[0]?.content} alt="Atendimento" width="50px"
                                        style={{minWidth: "50px"}}></img>
                                </td>
                                <td>
                                    <h6 className="text-light">{contactModels[0]?.name}</h6>
                                    <div className="text-light">{contactModels[0]?.initialContent}</div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </Col>
                <Col>
                    <table className="footer-left-align-table">
                        <thead></thead>
                        <tbody>
                            <tr className="table-primary">
                                <td>
                                    <img src={contactModels[1]?.content} alt="Central de Atendimento" width="50px"
                                        style={{minWidth: "50px"}}></img>
                                </td>
                                <td>
                                    <h6 className="text-light">{contactModels[1]?.name}</h6>
                                    <div className="text-light">{contactModels[1]?.initialContent}</div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </Col>
                <Col>
                    <table className="footer-left-align-table">
                        <thead></thead>
                        <tbody>
                            <tr className="table-primary">
                                <td>
                                    <img src={contactModels[2]?.content} alt="Email" width="50px"
                                        style={{minWidth: "50px"}}></img>
                                </td>
                                <td>
                                    <h6 className="text-light">{contactModels[2]?.name}</h6>
                                    <div className="text-light">{contactModels[2]?.initialContent}</div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </Col>
            </Row>
            <div className="large-vertical-combo-spacer"></div>
            <Row>
                <Col>
                    <div className="btn-group-vertical">
                        <img src="https://diblasipizzas.com/api/assets/dependent/17F16983F8F0099B8A6B2B5180E654F0D4D97824D04C7B7FB5C1D253543033DD/content" 
                        alt="Nos avalie no google!" style={{marginInline: "auto", justifySelf: "center"}}/>
                    </div>
                </Col>
            </Row>
            <div className="large-vertical-combo-spacer"></div>
            <Row>
                <Container style={{
                    width: "100%", 
                    display: "flex", 
                    flexWrap: "wrap", 
                    justifyContent: "center",
                    rowGap: "30px",
                }}>
                    <RAVerifiedSeal/>
                    <div style={{alignSelf: "center"}}>
                        <img src="https://diblasipizzas.com/api/assets/dependent/1E02FBFA17ED8631E3C952C8AE6CA1881089F4843D926B205DBFB562C877D899/content" alt="copyright"/>
                    </div>
                </Container>
            </Row>
            <div className="large-vertical-combo-spacer"></div>
            <Row>
                <Col>{new Date().getFullYear()} © DI BLASI FRANCHISING LTDA | CNPJ: 41.720.736/0001-11</Col>
            </Row>
            <div className="large-vertical-combo-spacer"></div>
        </Container>
    );
}